import { css, type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  ReactElement,
  ReactNode,
  PropsWithChildren,
} from 'react';
import {
  useState,
  Children,
  useEffect,
} from 'react';
import { breakpointSmallNoPaddingClass } from '../Page';
import type { TabProps } from './Tab';
import { colors } from '@/constants/colors';

export interface TabsProps {
  headStyle?: TabHeadStyle;
  overrideHeadCss?: SerializedStyles;
  overrideContainerCss?: SerializedStyles;
  index?: number;
  scrollToTop?: boolean;
  mobileChildLeftRightPadding?: string;
  onChange?: (index: number) => void;
}

export type TabHeadStyle = TabHeadProps & TabHeadItemProps;

type TabHeadProps = {
  height?: string,
  width?: string,
  baseColor?: string,
  borderRadius?: string,
  marginTop?: number,
  marginBottom?: number,
  align?: 'left' | 'right' | 'center',
  gap?: number,
};

type TabHeadItemProps = {
  backgroundColor?: string,
  selectedBackgroundColor?: string,
  selectedBorderThick?: number,
  selectedBorderLeftRightColor?: string,
  borderTopColor?: string,
  borderTopThick?: number,
  borderBottomColor?: string,
  borderBottomThick?: number,
  selectedBorderTopThick?: number,
  selectedBorderBottomThick?: number,
  selectedBorderColor?: string,
  borderRadius?: string,
  fontSize?: string,
  textColor?: string,
  selectedTextColor?: string,
  fontWeight?: number,
  selectedFontWeight?: number,
  lineHeight?: number | string,
  padding?: string,
  shouldApplyHoverEffect?: boolean,
};

export const normalTabHeadStyle: TabHeadStyle = {
  marginBottom: 50,
  selectedBorderTopThick: 3,
  selectedBorderBottomThick: 0,
  selectedBorderColor: colors.blue,
  borderBottomColor: colors.lightGrey2,
};

const Container = styled.div`
  width: 100%;
`;

export const TabsHeadDiv = styled.div<TabHeadProps>`
  display: flex;
  width: ${({ width }) => width};
  height: ${({ height }) => height || '66px' };
  margin: ${({ marginTop, marginBottom, align }) => `${marginTop || 0}px ${(align === 'left' || align === 'center') ? 'auto' : '0'} ${marginBottom || 0}px ${(align === 'right' || align === 'center') ? 'auto' : '0'}`};
  background-color: ${({ baseColor }) => baseColor || colors.transparent };
  border-radius: ${({ borderRadius }) => borderRadius };
  gap: ${({ gap }) => gap || 0}px;
`;

export const TabsHeadItemDiv = styled.div<TabHeadItemProps & { selected: boolean }>`
display: flex;
flex-basis: 100%;
padding: ${({ padding }) => padding || '0 ' };
border-width: ${({ selected, selectedBorderThick }) => selected ? selectedBorderThick ?? 1 : 0}px;
border-style: solid;
border-color: ${({ selected, selectedBorderLeftRightColor }) => selected ? (selectedBorderLeftRightColor || colors.lightGrey2) : colors.transparent };
border-top-width: ${({ selected, borderTopThick, selectedBorderTopThick }) => selected ? selectedBorderTopThick : borderTopThick || 0}px;
border-top-color: ${({ selected, borderTopColor, selectedBorderColor }) => selected ? selectedBorderColor : borderTopColor};
border-bottom-width: ${({ selected, borderBottomThick, selectedBorderBottomThick }) => selected ? selectedBorderBottomThick : borderBottomThick || 1}px;
border-bottom-color: ${({ selected, borderBottomColor, selectedBorderColor }) => selected ? selectedBorderColor : borderBottomColor};
background-color: ${({ selected, backgroundColor, selectedBackgroundColor }) => selected ? (selectedBackgroundColor || colors.transparent) : (backgroundColor || colors.whiteGrey4) };
border-radius: ${({ borderRadius }) => borderRadius };
cursor: pointer;
text-align: center;
word-break: keep-all;
span {
  margin: auto;
  color: ${({ selected, textColor, selectedTextColor }) => selected ? (selectedTextColor || colors.blue) : (textColor || colors.arsenic)};
  font-size: ${({ fontSize }) => (fontSize || '16px') };
  font-weight: ${({ selected, fontWeight, selectedFontWeight }) => selected ? (selectedFontWeight || 700) : (fontWeight || 400) };
  line-height: ${({ lineHeight }) =>
    typeof lineHeight === 'string' && lineHeight
      || typeof lineHeight === 'number' && `${lineHeight}px`
      || '24px'
};
}

${({
    shouldApplyHoverEffect,
    selectedTextColor,
    selectedBackgroundColor,
  }) => shouldApplyHoverEffect && css`
transition: all 0.2s ease-out;

> span {
  transition: all 0.2s ease-out;
}

&:hover {
  background-color: ${(selectedBackgroundColor || colors.transparent)};
  
  > span {
    color: ${(selectedTextColor || colors.blue)};
  }
}
`}
`;

export const describeTabElement = (child: ReactNode, index: number) => {
  const _child = child as ReactElement;
  if (!_child?.props) return {};
  const { tabTitle, tabIndex } = _child.props as TabProps;
  return {
    tabTitle,
    tabIndex: tabIndex ? tabIndex : index,
  };
};

const Tabs = ({
  children,
  headStyle,
  index,
  scrollToTop,
  onChange,
  overrideHeadCss,
  overrideContainerCss,
}: PropsWithChildren<TabsProps>) => {
  const [selected, setSelected] = useState(index ? index : 0);

  useEffect(() => {
    if (typeof index !== 'undefined') setSelected(index);
  }, [index]);

  return (
    <Container className={breakpointSmallNoPaddingClass} css={overrideContainerCss}>
      <TabsHeadDiv {...headStyle} css={overrideHeadCss}>
        {Children.map(children, (child, index) => {
          const { tabTitle, tabIndex } = describeTabElement(child, index);
          if (typeof tabIndex !== 'number') return <></>;
          return (
            <TabsHeadItemDiv
              {...headStyle}
              selected={selected === tabIndex}
              onClick={() => {
                setSelected(tabIndex);
                onChange?.(tabIndex);
                scrollToTop && scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span>{tabTitle}</span>
            </TabsHeadItemDiv>
          );
        })}
      </TabsHeadDiv>
      {Children.map(children, (child, index) => {
        const { tabIndex } = describeTabElement(child, index);
        if (typeof tabIndex !== 'number') return <></>;
        return (
          <>
            {selected === tabIndex && child}
          </>
        );
      })}
    </Container>
  );
};

export default Tabs;
